import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/api';
import { acceptJobOffer, requestJobStart, requestJobCompletion } from '../api/jobs';
import { useAuth } from '../context/AuthContext';
import { 
    FaBriefcase, FaPlayCircle, FaCheckCircle, FaEye, FaCheck, 
    FaPlay, FaFlagCheckered, FaClock, FaUserTie, FaCalendarAlt, FaMapMarkerAlt 
} from 'react-icons/fa';

function RunningJobs() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('my-jobs');
  const [offers, setOffers] = useState([]);
  const [runningJobs, setRunningJobs] = useState([]);
  const [finishedJobs, setFinishedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchJobs = useCallback(async () => {
    if (!user?.profileId) return;
    setLoading(true);
    setError('');
    try {
      const [offersRes, runningRes, finishedRes] = await Promise.all([
        apiClient.get(`/jobs/worker/${user.profileId}/offers`),
        apiClient.get(`/jobs/worker/${user.profileId}?status=running`), 
        apiClient.get(`/jobs/worker/${user.profileId}?status=completed`)
      ]);

      setOffers(offersRes.data || []);
      setRunningJobs(runningRes.data || []);
      setFinishedJobs(finishedRes.data || []);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setError('Failed to load jobs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user?.profileId]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleAcceptOffer = async (jobId) => {
    if (window.confirm("Accept this job offer?")) {
      try {
        await acceptJobOffer(jobId);
        fetchJobs();
      } catch (err) {
        alert("Failed to accept offer.");
      }
    }
  };

  const handleStartJob = async (jobId) => {
    if (window.confirm("Start this job now?")) {
      try {
        await requestJobStart(jobId);
        fetchJobs();
      } catch (err) {
        alert("Failed to start job.");
      }
    }
  };

  const handleFinishJob = async (jobId) => {
    if (window.confirm("Mark job as finished?")) {
      try {
        await requestJobCompletion(jobId);
        fetchJobs();
      } catch (err) {
        alert("Failed to finish job.");
      }
    }
  };

  const TabButton = ({ id, label, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-6 py-3 rounded-full text-sm font-bold transition-all ${
        activeTab === id 
        ? 'bg-slate-800 text-white shadow-md' 
        : 'bg-white text-slate-500 hover:bg-slate-100'
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
            activeTab === id ? 'bg-slate-600 text-white' : 'bg-slate-200 text-slate-600'
        }`}>
          {count}
        </span>
      )}
    </button>
  );

  const JobCard = ({ job, type }) => {
      const isOffer = type === 'offer';
      const isAccepted = job.status === 'accepted'; // For offers
      const contractStatus = job.contractStatus; // For running jobs
      const posterName = isOffer ? job.job_post?.poster?.name : job.poster?.name;
      const title = isOffer ? job.job_post?.title : job.title;
      const id = isOffer ? job.job_posts_id : job.id;
      const date = isOffer 
        ? job.job_post?.details?.[0]?.proposed_start_date 
        : job.endDate;

      return (
        <div className="bg-white p-6 rounded-[1.5rem] border border-slate-100 shadow-sm hover:shadow-md transition-all relative overflow-hidden group">
            <div className={`absolute top-0 left-0 w-full h-1.5 ${
                isOffer ? (isAccepted ? 'bg-green-500' : 'bg-blue-500') :
                contractStatus === 'active' ? 'bg-green-500' :
                contractStatus === 'completed' ? 'bg-slate-400' :
                'bg-yellow-500'
            }`}></div>
            
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h3 className="text-lg font-black text-slate-800 line-clamp-1 group-hover:text-emerald-600 transition-colors">{title}</h3>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">
                        {isOffer ? (isAccepted ? 'Scheduled' : 'New Offer') : 
                         contractStatus === 'active' ? 'Running' : 
                         contractStatus === 'completed' ? 'Completed' : 'Pending Action'}
                    </p>
                </div>
                <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-sm">
                    {posterName?.charAt(0) || 'E'}
                </div>
            </div>

            <div className="space-y-3 mb-6">
                <div className="flex items-center text-sm text-slate-600">
                    <FaUserTie className="mr-3 text-slate-400" />
                    <span className="font-medium">{posterName}</span>
                </div>
                <div className="flex items-center text-sm text-slate-600">
                    <FaCalendarAlt className="mr-3 text-slate-400" />
                    <span className="font-medium">{date ? new Date(date).toLocaleDateString() : 'Date TBD'}</span>
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <Link to={`/job/${id}`} className="w-full py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 hover:text-slate-900 transition-all text-center">
                    View Details
                </Link>

                {isOffer && !isAccepted && (
                    <button onClick={() => handleAcceptOffer(id)} className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 transition-all flex items-center justify-center">
                        <FaCheck className="mr-2" /> Accept Offer
                    </button>
                )}

                {((isOffer && isAccepted) || contractStatus === 'scheduled') && (
                    <button onClick={() => handleStartJob(id)} className="w-full py-2.5 rounded-xl bg-green-600 text-white font-bold text-sm hover:bg-green-700 transition-all flex items-center justify-center">
                        <FaPlay className="mr-2" /> Start Job
                    </button>
                )}

                {contractStatus === 'active' && (
                    <button onClick={() => handleFinishJob(id)} className="w-full py-2.5 rounded-xl bg-purple-600 text-white font-bold text-sm hover:bg-purple-700 transition-all flex items-center justify-center shadow-lg shadow-purple-200">
                        <FaFlagCheckered className="mr-2" /> Finish Job
                    </button>
                )}

                {(contractStatus === 'pending_start' || contractStatus === 'pending_end') && (
                    <div className="w-full py-2.5 rounded-xl bg-yellow-50 text-yellow-700 font-bold text-xs flex items-center justify-center border border-yellow-100">
                        <FaClock className="mr-2" /> Waiting for Buyer
                    </div>
                )}

                {/* Navigate Button for Worker */}
                {(contractStatus === 'active' || contractStatus === 'pending_start' || (isOffer && isAccepted)) && (
                    <Link 
                        to={`/job-tracking/${job.contractId || job.id}`} 
                        className="w-full py-2.5 rounded-xl bg-emerald-100 text-emerald-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-emerald-200 transition-all"
                    >
                        <FaMapMarkerAlt size={12} /> Navigate to Site
                    </Link>
                )}
            </div>
        </div>
      );
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10 font-sans">
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-10">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">My Jobs</h1>
                <p className="text-slate-500 mt-1">Track your active contracts and work history.</p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-3 mb-8">
                <TabButton id="my-jobs" label="Offers & Scheduled" count={offers.length} />
                <TabButton id="running-jobs" label="In Progress" count={runningJobs.length} />
                <TabButton id="finished-jobs" label="History" count={finishedJobs.length} />
            </div>

            {/* Content */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                    {[1,2,3].map(i => <div key={i} className="h-64 bg-slate-200 rounded-[1.5rem]"></div>)}
                </div>
            ) : error ? (
                <div className="p-8 bg-red-50 text-red-600 rounded-2xl">{error}</div>
            ) : (
                <>
                    {activeTab === 'my-jobs' && (
                        offers.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {offers.map(offer => <JobCard key={offer.id} job={offer} type="offer" />)}
                            </div>
                        ) : <EmptyState message="No pending offers." />
                    )}

                    {activeTab === 'running-jobs' && (
                        runningJobs.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {runningJobs.map(job => <JobCard key={job.id} job={job} type="running" />)}
                            </div>
                        ) : <EmptyState message="No active jobs." />
                    )}

                    {activeTab === 'finished-jobs' && (
                        finishedJobs.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {finishedJobs.map(job => <JobCard key={job.id} job={job} type="finished" />)}
                            </div>
                        ) : <EmptyState message="No job history yet." />
                    )}
                </>
            )}
        </div>
    </div>
  );
}

const EmptyState = ({ message }) => (
    <div className="text-center py-20 bg-white rounded-[2rem] border border-dashed border-slate-200">
        <FaBriefcase className="mx-auto text-slate-300 text-4xl mb-4" />
        <p className="text-slate-500 font-bold">{message}</p>
    </div>
);

export default RunningJobs;